import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useMobile from '../../hooks/useMobile';
import { paths } from '../../config/paths';
import { apiFetch } from '../../config/apiBase';
import { postSurveyOfflineDetail } from '../../api/survey/surveyOffline';
import {
  postSurveyOfflineApplyCheckInvite,
  postSurveyOfflineApplyConfirmData,
  postSurveyOfflineApplyConfirmFinal,
  postSurveyOfflineApplyLegacySubmit,
  postSurveyOfflineApplyMyInfo,
  postSurveyOfflineApplyPreAnswers,
  postSurveyOfflineApplyStart,
} from '../../api/survey/surveyOfflineApply';
import { ensureSmsCallAgreeBeforeOfflineAction } from '../../utils/surveyOfflineSmsCallAgree';
import { useOfflineApplyLeaveGuard } from '../../hooks/useOfflineApplyLeaveGuard';
import { RESUME_OFFLINE_APPLY_STATE_KEY } from '../../utils/auth/postLoginNavigation';
import { useAuth } from '../../contexts/AuthProvider';
import { useSystemPopup } from '../common/SystemPopup';
import Spinner from '../common/Spinner';
import SurveyOfflineLocationMapPopup from './SurveyOfflineLocationMapPopup';
import { formatOfflineDetailInvPay, formatOfflineDetailPay } from '../../utils/surveyReward';

/** 3.0 CommonCodeService.commonCodeList.job (app-service.js 그대로). */
const COMMON_CODE_JOB = [
  { name: '전문직', value: '01' },
  { name: '경영직', value: '02' },
  { name: '사무직', value: '03' },
  { name: '서비스/영업/판매직', value: '04' },
  { name: '생산/기술직/노무직', value: '06' },
  { name: '교사/학원강사', value: '07' },
  { name: '공무원(공기업 포함)', value: '08' },
  { name: '학생', value: '09' },
  { name: '전업주부', value: '10' },
  { name: '농/임/어업', value: '11' },
  { name: '자영업', value: '13' },
  { name: '자유직/프리랜서', value: '14' },
  { name: '무직', value: '15' },
  { name: '기타', value: '12' },
];

/** 3.0 CommonCodeService.commonCodeList.dbGender */
const COMMON_CODE_DB_GENDER = [
  { name: '남성', value: '1' },
  { name: '여성', value: '2' },
];

/** 3.0 CommonCodeService.cellFirstNo */
const CELL_FIRST_NO_OPTIONS = [
  { value: '010', label: '010' },
  { value: '011', label: '011' },
  { value: '016', label: '016' },
  { value: '017', label: '017' },
  { value: '018', label: '018' },
  { value: '019', label: '019' },
];

/** 3.0 CommonCodeService.birthMonth */
const BIRTH_MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const n = i + 1;
  const value = n < 10 ? `0${n}` : String(n);
  return { value, label: `${n}월` };
});

const QUEST_SUFFIX = { 111: 'SA', 121: 'MA', 211: 'TS' };

/** 비회원 신청자 정보 — 브라우저 자동완성만 비활성. */
const APPLY_TEXT_AUTOCOMPLETE = 'off';

/** 3.0 offline.js fnPerformMoveRequestView — 패널 로그인 시에만 신청 여부(reqType) 차단. */
function getOfflineApplyBlockReason(detail, forPanelMember = false) {
  if (!detail?.srvNo) return null;
  if (detail.allowReqYn !== 'Y') return 'not-period';
  if (!forPanelMember) return null;
  const newF = String(detail.beforeYn || 'Y').toUpperCase() === 'N';
  if (newF && Number(detail.reqType) === 3) return 'already';
  if (!newF && String(detail.isRequestedYn || '').toUpperCase() === 'Y') return 'already';
  return null;
}

/** StrictMode 이중 실행 시 동일 좌담회 진입 알림 1회만 (모듈 스코프). */
const offlineApplyEntryGuardShown = new Set();
/** 동일 srvReqNo·단계(questions|confirm) 안내 팝업 1회만 (모듈 스코프). */
const offlineApplyGuidePopupShown = new Set();
let __prevOfflineApplySrvNoForGuard;

/** 개편 플로우 사전질문 진입 안내 — 확인 화면 없이 [신청하기]로 최종 완료. */
const OFFLINE_APPLY_QUESTIONS_GUIDE = (
  <>
    마지막 화면에서 [신청하기]를 누르셔야<br />
    신청이 완료되오니 꼭 확인해 주세요!
  </>
);

/** 레거시(beforeYn=Y) 확인 화면 [신청완료] 안내. */
const OFFLINE_APPLY_COMPLETE_GUIDE = (
  <>
    마지막 화면에서 [신청완료]를 누르셔야<br />
    신청이 완료되오니 꼭 확인해 주세요!
  </>
);

/**
 * 3.0 offline.html load-popup — 개편(beforeYn=N) 사전질문 진입 시 안내.
 * @param {string} token 예: `${srvReqNo}|questions`
 */
function showOfflineApplyGuidePopup(openSystemPopup, token) {
  if (token) {
    if (offlineApplyGuidePopupShown.has(token)) return;
    offlineApplyGuidePopupShown.add(token);
  }
  openSystemPopup({
    type: 'alert',
    content: (
      <>
        <p className="strong-text">
          응답 중 뒤로가기 불가하며<br />
          작성하신 내용은 수정 불가능 합니다!
        </p>
        <p className="light-text">{OFFLINE_APPLY_QUESTIONS_GUIDE}</p>
      </>
    ),
    confirmText: '확인',
  });
}

/**
 * 개편 플로우 — confirm-data 검증 후 confirm-final 로 신청 1회 완료 (확인 화면 생략).
 * @param {() => void} onSuccess 신청 완료 후 알림·목록 이동 (이탈 가드 해제 포함).
 */
async function completeNewFlowOfflineApply(srvNo, srvReqNo, asGuest, openSystemPopup, onSuccess) {
  const cj = await postSurveyOfflineApplyConfirmData(srvNo, srvReqNo, asGuest).catch(() => ({}));
  if (cj.result !== 'success') {
    openSystemPopup({ type: 'alert', content: <p className="strong-text">확인 정보를 불러오지 못했습니다.</p> });
    return;
  }
  const json = await postSurveyOfflineApplyConfirmFinal(srvNo, srvReqNo, asGuest).catch(() => ({}));
  if (json.result === 'success') {
    onSuccess();
    return;
  }
  openSystemPopup({ type: 'alert', content: <p className="strong-text">최종 확인 처리에 실패했습니다.</p> });
}

function formatDateKo(isoOrDate) {
  if (isoOrDate == null || isoOrDate === '') return '';
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

function isCustomLocation(location) {
  if (!location) return false;
  return location.includes('본사') || location.includes('별관');
}

function mapTypeFromLocation(location) {
  if (!location) return 1;
  if (location.includes('별관')) return 2;
  return 1;
}

function needsIntroducer(detail) {
  const inv = String(detail?.invPayTpCd ?? '');
  return inv !== '' && inv !== 'N';
}

function normalizeDigits8(s) {
  const d = String(s || '').replace(/\D/g, '');
  return d.length >= 8 ? d.slice(0, 8) : d;
}

/** 3.0 CommonCodeService birthYearNew: 현재년 −14 ~ −100. */
function buildOfflineBirthYearOptions() {
  const y0 = new Date().getFullYear();
  const list = [];
  for (let i = 14; i <= 100; i += 1) {
    const v = y0 - i;
    list.push({ value: String(v), label: `${v}년` });
  }
  return list;
}

/** 3.0 offline.js fnCheckMonth — 해당 월의 말일까지 일 옵션. */
function buildOfflineBirthDayOptions(birthYearStr, birthMonthStr) {
  const y = Number(birthYearStr);
  const m = Number(birthMonthStr);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return [];
  const last = new Date(y, m, 0).getDate();
  const out = [];
  for (let d = 1; d <= last; d += 1) {
    const value = d < 10 ? `0${d}` : String(d);
    out.push({ value, label: `${d}일` });
  }
  return out;
}

/** 비로그인 생년월일 → YYYYMMDD (3.0 requestSurveyOffline 직전 조합). */
function composeGuestBirthdayYmd(b1, b2, b3) {
  const y = String(b1 || '').replace(/\D/g, '');
  const mo = String(b2 || '').replace(/\D/g, '');
  const d = String(b3 || '').replace(/\D/g, '');
  if (y.length !== 4 || mo.length !== 2 || d.length !== 2) return '';
  return `${y}${mo}${d}`;
}

function isValidCalendarYmd(ymd8) {
  if (!ymd8 || ymd8.length !== 8) return false;
  const y = Number(ymd8.slice(0, 4));
  const m = Number(ymd8.slice(4, 6));
  const d = Number(ymd8.slice(6, 8));
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/** 비로그인 휴대폰 → 3.0과 동일 하이픈 조합. */
function composeGuestCellNo(c1, c2, c3) {
  const a = String(c1 || '').trim();
  const b = String(c2 || '').replace(/\D/g, '').slice(0, 4);
  const c = String(c3 || '').replace(/\D/g, '').slice(0, 4);
  if (!a || !b || !c) return '';
  return `${a}-${b}-${c}`;
}

/** 3.0 CommonService.fnValidateCellNo */
function validateCellNoHyphen(cellNo) {
  return /^\d{3}-\d{3,4}-\d{4}$/.test(String(cellNo || ''));
}

/** 3.0 offline.js nameChk — 한글·영문·공백 외 문자 차단용 검사. */
function guestReqNmHasInvalidChar(name) {
  return /[0-9~!@#$%<>^&*()\-=+_']/g.test(String(name || ''));
}

/** 3.0 requestUserInfo.age 와 동일: 만 나이(생년월일 YYYYMMDD). */
function computeKoreanManAge(birthYmd8) {
  const d = normalizeDigits8(birthYmd8);
  if (d.length !== 8) return '';
  const y = Number(d.slice(0, 4));
  const mo = Number(d.slice(4, 6));
  const day = Number(d.slice(6, 8));
  if (!y || mo < 1 || mo > 12 || day < 1 || day > 31) return '';
  const bd = new Date(y, mo - 1, day);
  if (Number.isNaN(bd.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - bd.getFullYear();
  const md = today.getMonth() - bd.getMonth();
  if (md < 0 || (md === 0 && today.getDate() < bd.getDate())) age -= 1;
  return age >= 0 ? String(age) : '';
}

/** JOB_CD / M.JOB 을 3.0 job 코드(2자리 문자열)로 맞춤. */
function normalizeJobCdKey(raw) {
  const t = String(raw ?? '')
    .trim()
    .replace(/\s/g, '');
  if (!t || t === '00' || t === '0000') return '';
  if (/^\d+$/.test(t) && t.length <= 2) return t.padStart(2, '0');
  return t;
}

/**
 * 3.0 app-filter.js `commonCode` 필터와 동일: 목록에서 value === 일치 시 name, 없으면 ''.
 * @param {{ name: string, value: string }[]} list
 * @param {unknown} rawValue
 * @param {(raw: unknown) => string} [normalizeKey]
 */
function commonCodeName(list, rawValue, normalizeKey) {
  const key = normalizeKey ? normalizeKey(rawValue) : String(rawValue ?? '').trim();
  if (key === '') return '';
  const hit = list.find((item) => String(item.value) === key);
  return hit ? hit.name : '';
}

/** 3.0 `| commonCode:'job'` */
function commonCodeJobName(raw) {
  return commonCodeName(COMMON_CODE_JOB, raw, normalizeJobCdKey);
}

/** 3.0 `| commonCode:'dbGender'` */
function commonCodeDbGenderName(raw) {
  return commonCodeName(COMMON_CODE_DB_GENDER, raw, (x) => String(x ?? '').trim());
}

/** 3.0 offline.js fnNextQuest 검증·행 구성 — 실패 시 메시지, 성공 시 insertAnswer/pre-answers 본문. */
function validateAndBuildPreQuestRows(quest, ansState, srvNo, srvReqNo) {
  const qt = String(quest.questType ?? '');
  const qtext = quest.questNoText;
  const cases = quest.caseList || [];
  const answer = ansState?.[qtext];

  if (qt === '111') {
    if (!answer || answer.val == null || answer.val === '') {
      return { ok: false, message: `${qtext}) 질문에 응답해주세요.` };
    }
    let matched = false;
    for (const qc of cases) {
      if (answer.val !== qc.controlValue) continue;
      matched = true;
      if (qc.etcInfo && (!answer.etc || !String(answer.etc[qc.controlValue] || '').trim())) {
        const qid = qc.questNoText != null && String(qc.questNoText).trim() !== '' ? qc.questNoText : qtext;
        return { ok: false, message: `${qid}) ${qc.caseText}의 답변을 입력해 주세요.` };
      }
    }
    if (!matched) {
      return { ok: false, message: `${qtext}) 질문에 응답해주세요.` };
    }
    const rows = [];
    for (const qc of cases) {
      if (answer.val !== qc.controlValue) continue;
      rows.push({ srvNo, srvReqNo, questNoText: qtext, ansNo: qc.controlVar, ansValue: answer.val });
      if (qc.etcInfo && answer.etc) {
        rows.push({
          srvNo,
          srvReqNo,
          questNoText: qtext,
          ansNo: qc.etcInfo.controlVar,
          ansValue: answer.etc[qc.controlValue],
        });
      }
    }
    return rows.length ? { ok: true, rows } : { ok: false, message: `${qtext}) 질문에 응답해주세요.` };
  }

  if (qt === '121') {
    if (!answer) {
      return { ok: false, message: `${qtext}) 질문에 응답해주세요.` };
    }
    const sorted = [...cases].sort((a, b) => String(a.controlVar).localeCompare(String(b.controlVar)));
    let ma = '';
    let any = false;
    for (const qc of sorted) {
      if (qc.caseType === 'TS') continue;
      const on = !!(answer.val && answer.val[qc.controlValue]);
      ma += on ? '1' : '0';
      if (on) {
        any = true;
        if (qc.etcInfo && (!answer.etc || !String(answer.etc[qc.controlValue] || '').trim())) {
          const qid = qc.questNoText != null && String(qc.questNoText).trim() !== '' ? qc.questNoText : qtext;
          const etcLabel = qc.etcInfo?.caseText || qc.caseText || '';
          return { ok: false, message: `${qid}) ${etcLabel}의 답변을 입력해 주세요.` };
        }
      }
    }
    if (!any) {
      return { ok: false, message: `${qtext}) 질문에 응답해주세요.` };
    }
    const rows = [];
    rows.push({
      srvNo,
      srvReqNo,
      questNoText: qtext,
      ansNo: `${qtext}${QUEST_SUFFIX[121]}`,
      ansValue: ma,
    });
    for (const qc of sorted) {
      if (qc.caseType === 'TS') continue;
      if (answer.val && answer.val[qc.controlValue] && qc.etcInfo && answer.etc && answer.etc[qc.controlValue]) {
        rows.push({
          srvNo,
          srvReqNo,
          questNoText: qtext,
          ansNo: qc.etcInfo.controlVar,
          ansValue: answer.etc[qc.controlValue],
        });
      }
    }
    return { ok: true, rows };
  }

  if (qt === '211') {
    if (!answer) {
      return { ok: false, message: `${qtext}) 질문에 응답해주세요.` };
    }
    const rows = [];
    for (const qc of cases) {
      if (qc.caseType !== 'TS') continue;
      const v = answer.val && answer.val[qc.controlValue];
      if (!v || !String(v).trim()) {
        const qid = qc.questNoText != null && String(qc.questNoText).trim() !== '' ? qc.questNoText : qtext;
        return { ok: false, message: `${qid}) 질문에 응답해주세요.` };
      }
      rows.push({ srvNo, srvReqNo, questNoText: qtext, ansNo: qc.controlVar, ansValue: v });
    }
    return rows.length ? { ok: true, rows } : { ok: false, message: `${qtext}) 질문에 응답해주세요.` };
  }

  return { ok: false, message: `${qtext}) 질문에 응답해주세요.` };
}

/**
 * 개편 플로우 [신청하기] 직전 form 요약용.
 * 로컬 qAnswers → 3.0 confirm의 questNoText / questText / ansTxt 형태.
 * @returns {{ questNoText: string, questText: string, ansTxt: string[] }[]}
 */
function buildPreAnswerDisplayRows(qList, qAnswers) {
  const rows = [];
  for (const quest of qList || []) {
    const qt = String(quest.questType ?? '');
    const qtext = quest.questNoText;
    if (qtext == null || String(qtext).trim() === '') continue;
    const cases = quest.caseList || [];
    const answer = qAnswers?.[qtext];
    if (!answer) continue;

    if (qt === '111') {
      let anstxt = '';
      for (const qc of cases) {
        if (answer.val !== qc.controlValue) continue;
        anstxt += qc.caseText || '';
        const etcVal = qc.etcInfo && answer.etc?.[qc.controlValue];
        if (etcVal != null && String(etcVal).trim() !== '') {
          anstxt += `(${String(etcVal).trim()})`;
        }
      }
      if (anstxt) {
        rows.push({
          questNoText: String(qtext),
          questText: quest.questText || '',
          ansTxt: [anstxt],
        });
      }
      continue;
    }

    if (qt === '121') {
      const parts = [];
      const sorted = [...cases].sort((a, b) => String(a.controlVar).localeCompare(String(b.controlVar)));
      for (const qc of sorted) {
        if (qc.caseType === 'TS') continue;
        if (!(answer.val && answer.val[qc.controlValue])) continue;
        let t = qc.caseText || '';
        const etcVal = qc.etcInfo && answer.etc?.[qc.controlValue];
        if (etcVal != null && String(etcVal).trim() !== '') {
          t += `(${String(etcVal).trim()})`;
        }
        parts.push(t);
      }
      if (parts.length) {
        rows.push({
          questNoText: String(qtext),
          questText: quest.questText || '',
          ansTxt: parts,
        });
      }
      continue;
    }

    if (qt === '211') {
      const parts = [];
      for (const qc of cases) {
        if (qc.caseType !== 'TS') continue;
        const v = answer.val && answer.val[qc.controlValue];
        if (v != null && String(v).trim() !== '') {
          parts.push(String(v).trim());
        }
      }
      if (parts.length) {
        rows.push({
          questNoText: String(qtext),
          questText: quest.questText || '',
          ansTxt: parts,
        });
      }
    }
  }
  return rows;
}

/** 3.0 offline.html id="radio{{ case.caseOrder }}" — 보기마다 controlVar(Q1SA)가 같을 수 있어 caseOrder 기준 key. */
function questionCaseReactKey(questNoText, c, index) {
  const ord = c?.caseOrder != null && String(c.caseOrder).trim() !== '' ? String(c.caseOrder) : null;
  const cv = c?.controlValue != null ? String(c.controlValue) : '';
  if (ord) return `${questNoText}-${ord}-${cv}`;
  const v = c?.controlVar != null ? String(c.controlVar) : 'case';
  return `${questNoText}-${v}-${cv}-${index}`;
}

function DetailBlock({ detail, isMobile, locationPopupOpen, setLocationPopupOpen, mapTp }) {
  const contRef = useRef(null);
  const payHtml = detail?.payType
    ? String(detail.payType)
        .replace(/<br\s*\/?>/gi, '<br />')
        .replace(/\r\n/g, '<br />')
        .replace(/\n/g, '<br />')
    : '';
  const rawTgtDesc = detail?.tgtDesc ?? detail?.tgt_desc;
  const tgtDescText =
    rawTgtDesc != null && String(rawTgtDesc).trim() !== ''
      ? String(rawTgtDesc).replace(/<br\s*\/?>/gi, '\n')
      : '';
  const regRange = (() => {
    if (!detail?.regStaDt && !detail?.regEndDt) return '';
    const a = formatDateKo(detail.regStaDt);
    const b = formatDateKo(detail.regEndDt);
    if (a && b && a !== b) return `${a} ~ ${b}`;
    return b || a;
  })();
  const srvRange =
    detail &&
    (() => {
      const sta = detail.srvStaDt;
      const end = detail.srvEndDt;
      if (!sta && !end) return '';
      if (sta && end && formatDateKo(sta) !== formatDateKo(end)) {
        return `${formatDateKo(sta)} ~ ${formatDateKo(end)}`;
      }
      return formatDateKo(end || sta);
    })();
  const resPayLabel = detail
    ? formatOfflineDetailPay({
        minVal: detail.minResPayVal,
        maxVal: detail.maxResPayVal,
        tpCd: detail.resPayTpCd,
      })
    : '';
  const invPayLabel = detail
    ? formatOfflineDetailInvPay({
        minVal: detail.minInvPayVal,
        maxVal: detail.maxInvPayVal,
        tpCd: detail.invPayTpCd,
      })
    : '';
  const showTarget =
    (((detail?.grpList || []).length > 0 && detail.grpMultiYn !== 'E') || Boolean(tgtDescText));
  const sortedGrp =
    detail != null
      ? [...(detail.grpList || [])]
          .filter(() => detail.grpMultiYn !== 'E')
          .sort((a, b) => (Number(a.grpOrd) || 0) - (Number(b.grpOrd) || 0))
      : [];

  useEffect(() => {
    const el = contRef.current;
    if (!el || !detail?.cont) return;
    const origin = window.location.origin;
    el.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
        if (src.startsWith('/')) img.setAttribute('src', origin + src);
        else img.setAttribute('src', `${origin}/${src}`);
      }
    });
  }, [detail?.cont]);

  const openMap = () => {
    if (!detail?.location) return;
    if (!isCustomLocation(detail.location)) {
      window.alert('해당 장소는 지도가 제공되지 않습니다. 양해 부탁드립니다.');
      return;
    }
    setLocationPopupOpen(true);
  };

  return (
    <>
      <section className="panel-detail">
        <h3 className="page-subtitle">{detail.srvNm || '좌담회'}</h3>
        <table className="data__table data__table--normal" aria-label="좌담회 상세 내용">
          <colgroup>
            <col width="18%" />
            <col width="auto" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row">내용</th>
              <td>
                <div ref={contRef} className="txConRow" dangerouslySetInnerHTML={{ __html: detail.cont || '' }} />
                <dl className="box__info mgt-md">
                  <dt className="ir before i-info-red color-danger">좌담회 신청 시 유의사항</dt>
                  <dd>참석 대상이 될 수 있는 분께만 개별적으로 연락을 드립니다.</dd>
                  <dd>대상자로 선정되면 확정 문자를 드리며, 문자를 받지 못하신 분은 참석할 수 없습니다.</dd>
                  <dd>전화 연결이 되지 않으면 다음 신청자에게 처리가 넘어갑니다.</dd>
                </dl>
              </td>
            </tr>
            <tr>
              <th scope="row">신청기간</th>
              <td>{regRange}</td>
            </tr>
            <tr>
              <th scope="row">진행일</th>
              <td>{srvRange}</td>
            </tr>
            <tr>
              <th scope="row">장소</th>
              <td>
                {detail.location || ''}
                {isCustomLocation(detail.location) && (
                  <>
                    {' '}
                    <button type="button" className="btn btn--sm btn--outline-light" onClick={openMap}>
                      지도보기
                    </button>
                  </>
                )}
              </td>
            </tr>
            <tr>
              <th scope="row">소요시간</th>
              <td>{detail.reqreTime || ''}</td>
            </tr>
            {resPayLabel && (
              <tr>
                <th scope="row">사례비</th>
                <td>{resPayLabel}</td>
              </tr>
            )}
            {invPayLabel && (
              <tr>
                <th scope="row">소개비</th>
                <td>{invPayLabel}</td>
              </tr>
            )}
            <tr>
              <th scope="row">지급방법</th>
              <td dangerouslySetInnerHTML={{ __html: payHtml }} />
            </tr>
            <tr>
              <th scope="row">담당자</th>
              <td>
                {[detail.mgrNm, detail.mgrEmail, detail.mgrCellNo].filter(Boolean).join(' / ')}{' '}
                <span className={`color-light-gray ${isMobile ? 'block mgt-xs' : ''}`}>
                  (문의가능시간 : 10:00~12:00, 13:00~18:00)
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {showTarget && (
        <section className="panel-detail">
          <h3 className="page-subtitle">좌담회 대상 분류기준</h3>
          <dl className="panel-detail__criteria" aria-label="좌담회 대상 분류기준">
            {tgtDescText && (
              <div className="panel-detail__criteria-row">
                <dt>공통대상</dt>
                <dd>
                  <div className="panel-detail__tgt-desc">{tgtDescText}</div>
                </dd>
              </div>
            )}
            {sortedGrp.map((g, idx) => (
              <div key={`grp-${g.grpOrd}-${idx}`} className="panel-detail__criteria-row">
                <dt>{idx + 1} 그룹</dt>
                <dd className={g.closYn === 'Y' ? 'color-light-gray' : ''}>
                  {g.grpNm}
                  {g.closYn === 'Y' && <span className="color-danger mgl-sm">[마감]</span>}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <SurveyOfflineLocationMapPopup
        open={locationPopupOpen}
        onClose={() => setLocationPopupOpen(false)}
        mapTp={mapTp}
      />
    </>
  );
}

/**
 * 좌담회 신청 단계 UI (3.0 offline.html write/request/confirm 흐름).
 * @param {{ srvNo: string | null, embedded?: boolean, showDetailBlock?: boolean, showPageHeading?: boolean, onExitApply?: () => void, onStepChange?: (step: string) => void }} props
 */
export default function SurveyOfflineApplyFlow({
  srvNo,
  embedded = false,
  showDetailBlock = true,
  showPageHeading = true,
  onExitApply,
  onStepChange,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobile();
  const { isLoggedIn, accessToken, user } = useAuth();
  const { open: openSystemPopup } = useSystemPopup();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationPopupOpen, setLocationPopupOpen] = useState(false);
  const [mapTp, setMapTp] = useState(1);

  const [step, setStep] = useState(() => (isLoggedIn ? 'form' : 'entry'));
  const [memberProfile, setMemberProfile] = useState(null);
  /** 신청 최종 완료 후 SPA 이탈 가드 재차단 방지 */
  const [applyFinished, setApplyFinished] = useState(false);

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  /** 3.0 offline.js write/confirm — entry·상세만 보기 제외, beforeunload 미등록. */
  const leaveGuardEnabled =
    !applyFinished && (step === 'form' || step === 'questions' || step === 'confirm');
  const { confirmLeave } = useOfflineApplyLeaveGuard(leaveGuardEnabled);

  const goToPanelList = useCallback(() => {
    if (embedded && onExitApply) {
      onExitApply();
    }
    navigate(paths.survey.panel);
  }, [embedded, navigate, onExitApply]);

  const leaveToPanel = useCallback(() => {
    confirmLeave();
    goToPanelList();
  }, [confirmLeave, goToPanelList]);

  /** 개편 플로우 신청 완료 — 이탈 가드 해제 후 완료 알림 1회만 표시 */
  const finishNewFlowApplySuccess = useCallback(() => {
    setApplyFinished(true);
    confirmLeave();
    openSystemPopup({
      type: 'alert',
      content: <p className="strong-text">신청이 완료되었습니다.</p>,
      onConfirm: goToPanelList,
    });
  }, [confirmLeave, goToPanelList, openSystemPopup]);

  const [agreeYn, setAgreeYn] = useState(false);
  const [reqNm, setReqNm] = useState('');
  const [gender, setGender] = useState('');
  const [jobCd, setJobCd] = useState('');
  /** 비로그인 3.0 requestFormData.birth1/2/3 */
  const [birth1, setBirth1] = useState('');
  const [birth2, setBirth2] = useState('');
  const [birth3, setBirth3] = useState('');
  /** 비로그인 3.0 requestFormData.cellNo1/2/3 */
  const [cellNo1, setCellNo1] = useState('');
  const [cellNo2, setCellNo2] = useState('');
  const [cellNo3, setCellNo3] = useState('');
  const [invUserId, setInvUserId] = useState('');
  const [noneInv, setNoneInv] = useState(false);
  const [invVerified, setInvVerified] = useState(false);
  const [grpPick, setGrpPick] = useState({});

  const [legacyAnswers, setLegacyAnswers] = useState({});

  const [srvReqNo, setSrvReqNo] = useState('');
  const [qIndex, setQIndex] = useState(0);
  const [qAnswers, setQAnswers] = useState({});

  const [confirmData, setConfirmData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  /** 3.0 qestnListYn==='Y' 이후(레거시): 그룹행 숨김·레거시 질문행 표시. */
  const [legacyFormLocked, setLegacyFormLocked] = useState(false);
  /** 3.0 nowQuestNoText === 'end' — 사전 질문 모두 저장 후 폼에서 [신청하기]로 확인 단계 진입. */
  const [preQuestionsComplete, setPreQuestionsComplete] = useState(false);

  const beforeYn = detail?.beforeYn;
  const newFlow = String(beforeYn || 'Y').toUpperCase() === 'N';
  /** detail.qestnList 스냅샷 — 빈 배열 참조 안정화 */
  const qList = useMemo(() => detail?.qestnList || [], [detail?.qestnList]);
  /** 3.0 qestnListYn==='Y' && beforeYn==='N' — 1차 저장 후 비회원 입력 잠금 */
  const guestWriteLocked = !isLoggedIn && newFlow && !!srvReqNo;
  /** 3.0 offline.html 그룹 행: qestnListYn==='N' 일 때만. */
  const showGroupRow =
    ((newFlow && !srvReqNo) || (!newFlow && !legacyFormLocked)) &&
    (detail?.grpList || []).length > 0 &&
    detail?.grpMultiYn !== 'E';
  /** 레거시 사전 질문: qestnListYn==='Y' && beforeYn==='Y'. */
  const showLegacyQuestionRows = !newFlow && legacyFormLocked;
  const showFormNextBtn = (newFlow && !srvReqNo) || (!newFlow && !legacyFormLocked);
  const showFormSubmitBtn = (newFlow && !!srvReqNo) || (!newFlow && legacyFormLocked);
  /** 사전설문 모두 응답(end) — 완료 안내 + 검증 안내 추가 노출 */
  const showPreSurveyCompleteNotice =
    newFlow && !!srvReqNo && qList.length > 0 && preQuestionsComplete;

  const loadDetail = useCallback(async () => {
    if (!srvNo) return null;
    return postSurveyOfflineDetail(srvNo, { asGuest: !isLoggedIn });
  }, [srvNo, isLoggedIn]);

  useEffect(() => {
    let c = false;
    (async () => {
      setLoading(true);
      try {
        const json = await loadDetail();
        if (!c) setDetail(json);
      } catch {
        if (!c) setDetail(null);
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [loadDetail, isLoggedIn]);

  useEffect(() => {
    setLegacyFormLocked(false);
    setPreQuestionsComplete(false);
    setApplyFinished(false);
    setSrvReqNo('');
    setQIndex(0);
    setQAnswers({});
    setGrpPick({});
    setConfirmData(null);
  }, [srvNo]);

  const prevLoggedInRef = useRef(undefined);
  /** React disabled 반영 전 연속 클릭·중복 API 완료 방지. */
  const writeNextInFlightRef = useRef(false);
  const writeSubmitInFlightRef = useRef(false);

  /** 로그인/로그아웃 전환 시 신청 단계·입력·진입 가드 초기화 (3.0 세션 전환과 동일). */
  useEffect(() => {
    if (prevLoggedInRef.current === isLoggedIn) return;
    const hadPriorAuth = prevLoggedInRef.current !== undefined;
    prevLoggedInRef.current = isLoggedIn;
    if (!hadPriorAuth) return;

    offlineApplyEntryGuardShown.clear();
    offlineApplyGuidePopupShown.clear();
    setApplyFinished(false);
    setStep(isLoggedIn ? 'form' : 'entry');
    setMemberProfile(null);
    setLegacyFormLocked(false);
    setPreQuestionsComplete(false);
    setSrvReqNo('');
    setQIndex(0);
    setQAnswers({});
    setGrpPick({});
    setConfirmData(null);
    setAgreeYn(false);
    setReqNm('');
    setGender('');
    setJobCd('');
    setBirth1('');
    setBirth2('');
    setBirth3('');
    setCellNo1('');
    setCellNo2('');
    setCellNo3('');
    setInvUserId('');
    setNoneInv(false);
    setInvVerified(false);
    setLegacyAnswers({});
  }, [isLoggedIn]);

  useEffect(() => {
    if (__prevOfflineApplySrvNoForGuard !== srvNo) {
      offlineApplyEntryGuardShown.clear();
      offlineApplyGuidePopupShown.clear();
      __prevOfflineApplySrvNoForGuard = srvNo;
    }
  }, [srvNo]);

  /** 3.0 fnPerformMoveRequestView — 상세 로드 직후 신청기간·이미 신청 여부 알림. */
  useEffect(() => {
    if (!detail?.srvNo || loading) return;
    const reason = getOfflineApplyBlockReason(detail, isLoggedIn);
    if (!reason) return;
    const token = `${detail.srvNo}|${reason}`;
    if (offlineApplyEntryGuardShown.has(token)) return;
    offlineApplyEntryGuardShown.add(token);
    if (reason === 'not-period') {
      openSystemPopup({
        type: 'alert',
        content: <p className="strong-text">현재는 신청기간이 아닙니다.</p>,
        onConfirm: leaveToPanel,
      });
      return;
    }
    openSystemPopup({
      type: 'alert',
      content: <p className="strong-text">이미 신청하신 좌담회입니다.</p>,
      onConfirm: leaveToPanel,
    });
  }, [detail, isLoggedIn, loading, leaveToPanel, openSystemPopup]);

  useEffect(() => {
    if (!isLoggedIn || !accessToken || !srvNo) return;
    let c = false;
    (async () => {
      const json = await postSurveyOfflineApplyMyInfo();
      if (c || !json) return;
      setMemberProfile(json);
      const nm = String(json.reqNm || '').trim() || String(user?.name || '').trim();
      setReqNm(nm);
      setGender(String(json.gender || '').trim());
      setJobCd(String(json.jobCd || '').trim());
    })();
    return () => {
      c = true;
    };
  }, [isLoggedIn, accessToken, srvNo, user?.name]);

  useEffect(() => {
    if (detail?.location) setMapTp(isCustomLocation(detail.location) ? mapTypeFromLocation(detail.location) : 1);
  }, [detail?.location]);

  const selectedGrpList = useMemo(() => {
    const keys = Object.keys(grpPick).filter((k) => grpPick[k]);
    return keys.map((k) => ({ grpOrd: Number(k) }));
  }, [grpPick]);

  /** 개편 form [신청하기] 직전 — 선택한 그룹 표시명 (로컬 grpPick). */
  const selectedGroupSummaryLabels = useMemo(() => {
    if (!detail || detail.grpMultiYn === 'E') return [];
    return selectedGrpList.map(({ grpOrd }) => {
      const g = (detail.grpList || []).find((x) => Number(x.grpOrd) === Number(grpOrd));
      return g?.grpNm ? String(g.grpNm) : `그룹 ${grpOrd}`;
    });
  }, [detail, selectedGrpList]);

  /** 개편 form [신청하기] 직전 — 사전응답 요약 (로컬 qAnswers). */
  const preAnswerDisplayRows = useMemo(
    () => buildPreAnswerDisplayRows(qList, qAnswers),
    [qList, qAnswers],
  );

  /** 개편: 1차 신청 후 form 복귀 시 선택 내용 요약 노출 */
  const showNewFlowSelectionSummary = newFlow && !!srvReqNo;

  /** 3.0 replaceTgtDesc — 신청 폼 공통대상 본문(없으면 빈 문자열). */
  const replaceTgtDescForForm = useMemo(() => {
    const raw = detail?.tgtDesc ?? detail?.tgt_desc;
    if (raw == null || String(raw).trim() === '') return '';
    return String(raw)
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/\r\n/g, '\n')
      .trim();
  }, [detail]);

  /** 3.0 CommonCodeService.birthYearNew / birthDay(월별). */
  const offlineBirthYearOptions = useMemo(() => buildOfflineBirthYearOptions(), []);
  const offlineBirthDayOptions = useMemo(() => buildOfflineBirthDayOptions(birth1, birth2), [birth1, birth2]);

  /** 패널 로그인 — 복귀 시 좌담회 신청 플로우(form) 자동 재개 */
  const openLogin = () => {
    navigate(paths.auth.login, {
      replace: true,
      state: {
        from: `${location.pathname}${location.search}${location.hash}`,
        [RESUME_OFFLINE_APPLY_STATE_KEY]: true,
      },
    });
  };

  /** 3.0 offline.js cancelPage — 확인 후 좌담회 목록(임베드 시 onExitApply). */
  const openCancelApplyFlow = useCallback(() => {
    openSystemPopup({
      type: 'confirm',
      content: (
        <p className="strong-text">
          취소하면 진행했던 신청절차가 초기화됩니다.<br />
          정말로 좌담회 신청 페이지를 나가시겠습니까?
        </p>
      ),
      confirmText: '확인',
      cancelText: '취소',
      onConfirm: () => {
        if (embedded) onExitApply?.();
        leaveToPanel();
      },
    });
  }, [embedded, leaveToPanel, onExitApply, openSystemPopup]);

  const checkInviteId = async () => {
    if (guestWriteLocked) return;
    if (!invUserId.trim()) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">소개자 아이디를 입력해 주세요.</p> });
      return;
    }
    const json = await postSurveyOfflineApplyCheckInvite({ invUserId: invUserId.trim() }, !isLoggedIn).catch(() => ({}));
    if (json.result === 'exist') {
      setInvVerified(true);
      openSystemPopup({
        type: 'confirm',
        content: (
          <p className="strong-text">
            소개하신 패널의 아이디가 <strong className="color-primary">{json.userId}</strong>
            ({json.name})이 맞습니까?
          </p>
        ),
        confirmText: '확인',
        cancelText: '취소',
        onConfirm: () => {},
      });
    } else {
      setInvVerified(false);
      openSystemPopup({
        type: 'alert',
        content: <p className="strong-text">좌담회 소개자가 아니거나 존재하지 않는 패널입니다.</p>,
      });
    }
  };

  const validateApplicantBlock = () => {
    if (isLoggedIn) return true;
    if (!agreeYn) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">개인정보 수집에 동의해주셔야 신청이 가능합니다.</p> });
      return false;
    }
    if (!reqNm.trim() || reqNm.trim().length < 2) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">이름을 입력해 주세요.</p> });
      return false;
    }
    if (guestReqNmHasInvalidChar(reqNm.trim())) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">한글,영문만 입력할 수 있습니다.</p> });
      return false;
    }
    if (!gender) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">성별을 선택해 주세요.</p> });
      return false;
    }
    if (!jobCd) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">직업을 선택해 주세요.</p> });
      return false;
    }
    if (!birth1 || !birth2 || !birth3) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">생년월일을 입력해 주세요.</p> });
      return false;
    }
    const ymd = composeGuestBirthdayYmd(birth1, birth2, birth3);
    if (!isValidCalendarYmd(ymd)) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">생년월일을 입력해 주세요.</p> });
      return false;
    }
    const cell = composeGuestCellNo(cellNo1, cellNo2, cellNo3);
    if (!validateCellNoHyphen(cell)) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">휴대폰 번호를 입력해 주세요.</p> });
      return false;
    }
    return true;
  };

  const validateIntroGuestOnly = () => {
    if (isLoggedIn || !needsIntroducer(detail)) return true;
    if (!noneInv && invUserId.trim() && !invVerified) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">소개자 인증을 진행해 주세요.</p> });
      return false;
    }
    if (!noneInv && !invUserId.trim()) {
      openSystemPopup({
        type: 'alert',
        content: (
          <p className="strong-text">
            소개자를 입력해 주세요.
            <br />
            소개자가 없으면 &quot;소개자 없음&quot; 체크해 주세요.
          </p>
        ),
      });
      return false;
    }
    return true;
  };

  const validateGroups = () => {
    const grps = detail?.grpList || [];
    if (grps.length > 0 && detail.grpMultiYn !== 'E' && selectedGrpList.length < 1) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">그룹을 한가지 이상 선택해 주세요.</p> });
      return false;
    }
    return true;
  };

  /** 3.0 offline.js fnMoveRequestView2 — 비로그인·소개·그룹만(레거시 사전질문 제외). */
  const validateMoveRequestView2 = () => {
    if (!isLoggedIn) {
      if (!validateApplicantBlock()) return false;
    }
    if (!validateIntroGuestOnly()) return false;
    if (!validateGroups()) return false;
    return true;
  };

  /** 3.0 offline.js fnRequestConfirm2 — 레거시 [신청하기] 시 전체 검증. */
  const validateLegacyLockedSubmit = () => {
    if (!isLoggedIn) {
      if (!agreeYn) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">개인정보 수집에 동의해주셔야 신청이 가능합니다.</p> });
        return false;
      }
      if (!reqNm.trim() || reqNm.trim().length < 2) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">이름을 입력해 주세요.</p> });
        return false;
      }
      if (guestReqNmHasInvalidChar(reqNm.trim())) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">한글,영문만 입력할 수 있습니다.</p> });
        return false;
      }
      if (!gender) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">성별을 선택해 주세요.</p> });
        return false;
      }
      if (!jobCd) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">직업을 선택해 주세요.</p> });
        return false;
      }
      if (!birth1 || !birth2 || !birth3) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">생년월일을 입력해 주세요.</p> });
        return false;
      }
      const ymd = composeGuestBirthdayYmd(birth1, birth2, birth3);
      if (!isValidCalendarYmd(ymd)) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">생년월일을 입력해 주세요.</p> });
        return false;
      }
      const cell = composeGuestCellNo(cellNo1, cellNo2, cellNo3);
      if (!validateCellNoHyphen(cell)) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">휴대폰 번호를 입력해 주세요.</p> });
        return false;
      }
    }
    if (!validateIntroGuestOnly()) return false;
    if (!validateGroups()) return false;
    for (const q of qList) {
      const ord = q.qestnOrd;
      if (ord == null) continue;
      if (!String(legacyAnswers[ord] || '').trim()) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">질문사항에 대한 답변을 모두 입력해 주세요.</p> });
        return false;
      }
    }
    return true;
  };

  const assertApplyNotBlocked = () => {
    const reason = getOfflineApplyBlockReason(detail, isLoggedIn);
    if (reason === 'not-period') {
      openSystemPopup({
        type: 'alert',
        content: <p className="strong-text">현재는 신청기간이 아닙니다.</p>,
        onConfirm: leaveToPanel,
      });
      return false;
    }
    if (reason === 'already') {
      openSystemPopup({
        type: 'alert',
        content: <p className="strong-text">이미 신청하신 좌담회입니다.</p>,
        onConfirm: leaveToPanel,
      });
      return false;
    }
    return true;
  };

  /** 3.0 fnMoveRequestView2 — 개편: apply/start, 레거시: qestnListYn 잠금만. */
  const handleWriteNext = async () => {
    if (!detail?.srvNo) return;
    if (!assertApplyNotBlocked()) return;
    if (!validateMoveRequestView2()) return;
    if (writeNextInFlightRef.current) return;

    writeNextInFlightRef.current = true;
    setSubmitting(true);
    try {
      if (newFlow) {
        const body = {
          srvNo: detail.srvNo,
          grpList: selectedGrpList,
          noneInvIdYn: noneInv || undefined,
          invUserId: noneInv ? undefined : invUserId.trim() || undefined,
        };
        if (!isLoggedIn) {
          body.reqNm = reqNm.trim();
          body.gender = gender;
          body.jobCd = jobCd;
          body.birthday = composeGuestBirthdayYmd(birth1, birth2, birth3);
          body.cellNo = composeGuestCellNo(cellNo1, cellNo2, cellNo3);
        }
        const json = await postSurveyOfflineApplyStart(body, !isLoggedIn).catch(() => ({}));
        if (json.result === 'not-valid') {
          openSystemPopup({
            type: 'alert',
            content: <p className="strong-text">신청할 수 없는 좌담회입니다.</p>,
            onConfirm: leaveToPanel,
          });
          return;
        }
        if (json.result === 'exist') {
          openSystemPopup({
            type: 'alert',
            content: <p className="strong-text">이미 신청 처리된 좌담회입니다.</p>,
            onConfirm: leaveToPanel,
          });
          return;
        }
        if (json.result !== 'success' || !json.srvReqNo) {
          openSystemPopup({ type: 'alert', content: <p className="strong-text">신청에 실패했습니다. 다시 시도해 주세요.</p> });
          return;
        }
        const nextSrvReqNo = String(json.srvReqNo);
        setSrvReqNo(nextSrvReqNo);
        if (!qList.length) {
          const okReceive = await ensureSmsCallAgreeBeforeOfflineAction({
            isLoggedIn,
            apiFetch,
            openSystemPopup,
            purpose: 'request',
          });
          if (!okReceive) return;
          await completeNewFlowOfflineApply(
            detail.srvNo,
            nextSrvReqNo,
            !isLoggedIn,
            openSystemPopup,
            finishNewFlowApplySuccess,
          );
        } else {
          setPreQuestionsComplete(false);
          setQIndex(0);
          setStep('questions');
          showOfflineApplyGuidePopup(openSystemPopup, `${nextSrvReqNo}|questions`);
        }
      } else {
        setLegacyFormLocked(true);
      }
    } finally {
      writeNextInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  /** 3.0 fnRequestConfirm / fnRequestConfirm2 — 개편: 사전질문 완료 후 1회 신청 완료, 레거시: 로컬 확인 화면. */
  const handleWriteSubmit = async () => {
    if (!detail?.srvNo) return;
    if (!assertApplyNotBlocked()) return;

    if (newFlow && srvReqNo) {
      if (qList.length > 0 && !preQuestionsComplete) {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">사전질문에 모두 응답해주세요.</p> });
        return;
      }
      if (writeSubmitInFlightRef.current) return;
      writeSubmitInFlightRef.current = true;
      setSubmitting(true);
      try {
        const okReceive = await ensureSmsCallAgreeBeforeOfflineAction({
          isLoggedIn,
          apiFetch,
          openSystemPopup,
          purpose: 'request',
        });
        if (!okReceive) return;
        await completeNewFlowOfflineApply(
          detail.srvNo,
          srvReqNo,
          !isLoggedIn,
          openSystemPopup,
          finishNewFlowApplySuccess,
        );
      } finally {
        writeSubmitInFlightRef.current = false;
        setSubmitting(false);
      }
      return;
    }

    if (!newFlow && legacyFormLocked) {
      if (!validateLegacyLockedSubmit()) return;
      if (isLoggedIn) {
        const okReceive = await ensureSmsCallAgreeBeforeOfflineAction({
          isLoggedIn,
          apiFetch,
          openSystemPopup,
          purpose: 'request',
        });
        if (!okReceive) return;
      }
      const grpList = selectedGrpList.map(({ grpOrd }) => {
        const g = (detail.grpList || []).find((x) => Number(x.grpOrd) === Number(grpOrd));
        return { grpOrd, grpNm: g?.grpNm };
      });
      const legacyQestnRows = qList
        .filter((q) => q.qestnOrd != null)
        .map((q) => ({
          qestnOrd: q.qestnOrd,
          qestnNm: q.qestnNm,
          answerCont: String(legacyAnswers[q.qestnOrd] || '').trim(),
        }));
      setConfirmData({
        __legacyLocal: true,
        reqInfo: isLoggedIn
          ? {
              reqId: String(memberProfile?.reqId || user?.id || '').trim() || 'panel',
              reqNm: String(memberProfile?.reqNm || '').trim() || String(user?.name || '').trim() || '—',
            }
          : {
              reqNm: reqNm.trim(),
              cellNo: composeGuestCellNo(cellNo1, cellNo2, cellNo3),
            },
        grpList,
        legacyGuestDetail: !isLoggedIn
          ? {
              genderLabel: commonCodeDbGenderName(gender),
              jobLabel: commonCodeJobName(jobCd),
              birthLabel: `${birth1}년 ${Number(birth2)}월 ${Number(birth3)}일`,
            }
          : null,
        legacyQestnRows,
        legacyIntro:
          !isLoggedIn && needsIntroducer(detail)
            ? { invUserId: noneInv ? '' : invUserId.trim(), noneInv }
            : null,
      });
      setStep('confirm');
    }
  };

  const currentQuest = qList[qIndex];

  const submitQuestionNext = async () => {
    if (!currentQuest || !srvReqNo) return;
    const built = validateAndBuildPreQuestRows(currentQuest, qAnswers, detail.srvNo, srvReqNo);
    if (!built.ok) {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">{built.message}</p> });
      return;
    }
    const json = await postSurveyOfflineApplyPreAnswers(built.rows, !isLoggedIn).catch(() => ({}));
    if (json.result !== 'success') {
      openSystemPopup({ type: 'alert', content: <p className="strong-text">설문 응답값 저장 중 문제가 발생했습니다.</p> });
      return;
    }
    if (qIndex + 1 < qList.length) {
      setQIndex((i) => i + 1);
    } else {
      setPreQuestionsComplete(true);
      setStep('form');
    }
  };

  const finalConfirm = async () => {
    if (!detail?.srvNo) return;
    setSubmitting(true);
    try {
      if (confirmData?.__legacyLocal) {
        const applicant = {
          srvNo: detail.srvNo,
          grpList: selectedGrpList,
          noneInvIdYn: noneInv || undefined,
          invUserId: noneInv ? undefined : invUserId.trim() || undefined,
        };
        if (!isLoggedIn) {
          applicant.reqNm = reqNm.trim();
          applicant.gender = gender;
          applicant.jobCd = jobCd;
          applicant.birthday = composeGuestBirthdayYmd(birth1, birth2, birth3);
          applicant.cellNo = composeGuestCellNo(cellNo1, cellNo2, cellNo3);
        }
        const legacyList = qList
          .filter((q) => q.qestnOrd != null)
          .map((q) => ({ qestnOrd: q.qestnOrd, answerCont: String(legacyAnswers[q.qestnOrd] || '').trim() }));
        const json = await postSurveyOfflineApplyLegacySubmit({ applicant, legacyQestnList: legacyList }, !isLoggedIn).catch(
          () => ({})
        );
        if (json.result === 'success') {
          finishNewFlowApplySuccess();
        } else if (json.result === 'exist') {
          openSystemPopup({
            type: 'alert',
            content: <p className="strong-text">이미 신청하신 좌담회입니다.</p>,
            onConfirm: leaveToPanel,
          });
        } else {
          openSystemPopup({ type: 'alert', content: <p className="strong-text">신청에 실패했습니다.</p> });
        }
        return;
      }

      const effectiveSrvReqNo = String(srvReqNo || confirmData?.reqInfo?.srvReqNo || '').trim();
      if (!effectiveSrvReqNo) {
        openSystemPopup({
          type: 'alert',
          content: <p className="strong-text">신청 정보를 찾을 수 없습니다. 처음부터 다시 시도해 주세요.</p>,
        });
        return;
      }
      const json = await postSurveyOfflineApplyConfirmFinal(detail.srvNo, effectiveSrvReqNo, !isLoggedIn).catch(() => ({}));
      if (json.result === 'success') {
        finishNewFlowApplySuccess();
      } else {
        openSystemPopup({ type: 'alert', content: <p className="strong-text">최종 확인 처리에 실패했습니다.</p> });
      }
    } finally {
      setSubmitting(false);
    }
  };

  /** 퍼블 PreQuestionSection — 사전 질문 단계 상단 요약 행(읽기 전용). */
  const renderApplicantSummaryRows = () => {
    if (isLoggedIn && memberProfile) {
      const age = computeKoreanManAge(memberProfile.birthday);
      const gName = commonCodeDbGenderName(memberProfile.gender);
      const ageGender = [gName, age ? `만 ${age}세` : ''].filter(Boolean).join(' / ') || '—';
      const reqNmDisplay = String(memberProfile.reqNm || '').trim() || String(user?.name || '').trim() || '—';
      const cellDisplay =
        memberProfile.cellNo != null && String(memberProfile.cellNo).trim() ? memberProfile.cellNo : '—';
      return (
        <>
          <tr>
            <th scope="row">이름</th>
            <td>{reqNmDisplay}</td>
          </tr>
          <tr>
            <th scope="row">
              나이 및 <span className="iblock mgl-xs">성별</span>
            </th>
            <td>{ageGender}</td>
          </tr>
          <tr>
            <th scope="row">
              휴대폰<span className="iblock mgl-xs">번호</span>
            </th>
            <td>{cellDisplay}</td>
          </tr>
          <tr>
            <th scope="row">직업</th>
            <td>{commonCodeJobName(memberProfile.jobCd)}</td>
          </tr>
        </>
      );
    }
    if (!isLoggedIn) {
      const ymd = composeGuestBirthdayYmd(birth1, birth2, birth3);
      const birthDisplay =
        ymd.length === 8 ? `${ymd.slice(0, 4)}.${ymd.slice(4, 6)}.${ymd.slice(6, 8)}` : '—';
      const cellDisplay = composeGuestCellNo(cellNo1, cellNo2, cellNo3) || '—';
      return (
        <>
          <tr>
            <th scope="row">이름</th>
            <td>{reqNm.trim() || '—'}</td>
          </tr>
          <tr>
            <th scope="row">성별</th>
            <td>{commonCodeDbGenderName(gender) || '—'}</td>
          </tr>
          <tr>
            <th scope="row">직업</th>
            <td>{commonCodeJobName(jobCd) || '—'}</td>
          </tr>
          <tr>
            <th scope="row">생년월일</th>
            <td>{birthDisplay}</td>
          </tr>
          <tr>
            <th scope="row">휴대폰 정보</th>
            <td>{cellDisplay}</td>
          </tr>
        </>
      );
    }
    return null;
  };

  const renderQuestionInputs = () => {
    if (!currentQuest) return null;
    const qt = String(currentQuest.questType);
    const qtext = currentQuest.questNoText;
    const cases = currentQuest.caseList || [];
    const st = qAnswers[qtext] || { val: qt === '121' ? {} : qt === '211' ? {} : '' };

    if (qt === '111') {
      return (
        <div className="form__group form__group--check flex-column">
          {cases.map((c, idx) => (
            <div key={questionCaseReactKey(qtext, c, idx)} className="panel-apply__choice-row">
              <label className="radio panel-apply__choice-label">
                <input
                  type="radio"
                  name={`q-${qtext}`}
                  checked={st.val === c.controlValue}
                  onChange={() =>
                    setQAnswers((prev) => ({
                      ...prev,
                      [qtext]: { val: c.controlValue, etc: prev[qtext]?.etc || {} },
                    }))
                  }
                />
                <span className="radio__label">{c.caseText}</span>
              </label>
              {c.etcInfo && st.val === c.controlValue && (
                <input
                  type="text"
                  className="input panel-apply__etc-input"
                  autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                  placeholder="기타 입력"
                  value={(st.etc && st.etc[c.controlValue]) || ''}
                  onChange={(e) =>
                    setQAnswers((prev) => ({
                      ...prev,
                      [qtext]: {
                        val: c.controlValue,
                        etc: { ...(prev[qtext]?.etc || {}), [c.controlValue]: e.target.value },
                      },
                    }))
                  }
                />
              )}
            </div>
          ))}
        </div>
      );
    }
    if (qt === '121') {
      return (
        <div className="form__group form__group--check flex-column">
          {cases
            .filter((c) => c.caseType !== 'TS')
            .map((c, idx) => (
              <div key={questionCaseReactKey(qtext, c, idx)} className="panel-apply__choice-row">
                <label className="checkbox panel-apply__choice-label">
                  <input
                    type="checkbox"
                    checked={!!(st.val && st.val[c.controlValue])}
                    onChange={(e) =>
                      setQAnswers((prev) => ({
                        ...prev,
                        [qtext]: {
                          val: { ...(prev[qtext]?.val || {}), [c.controlValue]: e.target.checked },
                          etc: { ...(prev[qtext]?.etc || {}) },
                        },
                      }))
                    }
                  />
                  <span className="checkbox__label">{c.caseText}</span>
                </label>
                {c.etcInfo && st.val && st.val[c.controlValue] && (
                  <input
                    type="text"
                    className="input panel-apply__etc-input"
                    autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                    placeholder="기타 입력"
                    value={(st.etc && st.etc[c.controlValue]) || ''}
                    onChange={(e) =>
                      setQAnswers((prev) => ({
                        ...prev,
                        [qtext]: {
                          val: { ...(prev[qtext]?.val || {}) },
                          etc: { ...(prev[qtext]?.etc || {}), [c.controlValue]: e.target.value },
                        },
                      }))
                    }
                  />
                )}
              </div>
            ))}
        </div>
      );
    }
    if (qt === '211') {
      return (
        <div className="form__group form__group--check flex-column">
          {cases
            .filter((c) => c.caseType === 'TS')
            .map((c, idx) => (
              <div key={questionCaseReactKey(qtext, c, idx)} className="mgt-sm">
                <label className="block mgb-xs">{c.caseText}</label>
                <textarea
                  className="textarea textarea--quiz full"
                  autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                  rows={5}
                  placeholder="여기에 답변을 입력해 주세요."
                  aria-label="사전 문항 답변"
                  value={(st.val && st.val[c.controlValue]) || ''}
                  onChange={(e) =>
                    setQAnswers((prev) => ({
                      ...prev,
                      [qtext]: {
                        val: { ...(prev[qtext]?.val || {}), [c.controlValue]: e.target.value },
                      },
                    }))
                  }
                />
              </div>
            ))}
        </div>
      );
    }
    return null;
  };

  if (!srvNo) {
    const noSrv = (
      <>
        {showPageHeading && <h2 className="page-title">좌담회 신청하기</h2>}
        <p className="strong-text">좌담회 번호가 없습니다.</p>
        <Link to={paths.survey.panel} className="btn btn--lg btn--outline-light btn--panel-offline-secondary mgt-md">
          목록으로
        </Link>
      </>
    );
    return embedded ? noSrv : <div className="panel-list-page">{noSrv}</div>;
  }

  if (loading) {
    const loadingEl = (
      <>
        {showPageHeading && <h2 className="page-title">좌담회 신청하기</h2>}
        <div className="activity-list-loading" aria-busy="true" aria-live="polite">
          <Spinner size="lg" />
        </div>
      </>
    );
    return embedded ? loadingEl : <div className="panel-list-page">{loadingEl}</div>;
  }

  if (!detail?.srvNo) {
    const notFound = (
      <>
        {showPageHeading && <h2 className="page-title">좌담회 신청하기</h2>}
        <p className="strong-text">좌담회를 찾을 수 없습니다.</p>
        <Link to={paths.survey.panel} className="btn btn--lg btn--outline-light btn--panel-offline-secondary mgt-md">
          목록으로
        </Link>
      </>
    );
    return embedded ? notFound : <div className="panel-list-page">{notFound}</div>;
  }

  const pageTitle =
    step === 'entry'
      ? '좌담회 참여하기'
      : !isLoggedIn
        ? '비패널 좌담회 신청하기'
        : '좌담회 신청하기';

  const main = (
    <>
      {showPageHeading && <h2 className="page-title">{pageTitle}</h2>}

      {step === 'entry' && (
        <section className="user-entry">
          <article className="user-entry__card user-entry__card--panel">
            <h3 className="title ir before i-user-panel">엠브레인 패널</h3>
            <div className="desc">
              <p>엠브레인 패널파워 패널이신가요?</p>
              <p>
                패널에게 우선 참여의 기회가 있으니, <span className="iblock">로그인 후 신청해 주세요.</span>
              </p>
            </div>
            <button type="button" className="btn btn--md btn--primary" onClick={openLogin}>
              패널 로그인
            </button>
          </article>
          <article className="user-entry__card user-entry__card--guest">
            <h3 className="title ir before i-user-guest">엠브레인 패널이 아님</h3>
            <div className="desc">
              <p>소개를 받으셨나요?</p>
              <p>
                아래 [비패널 신청하기]를 선택해 주세요. <span className="iblock">소개하신 패널의 아이디를 꼭 입력해 주세요.</span>
              </p>
            </div>
            <button
              type="button"
              className="btn btn--md btn--primary"
              onClick={() => {
                setStep('form');
              }}
            >
              비패널 신청하기
            </button>
          </article>
        </section>
      )}

      {(step === 'form' || step === 'questions' || step === 'confirm') && showDetailBlock && (
        <>
          <DetailBlock
            detail={detail}
            isMobile={isMobile}
            locationPopupOpen={locationPopupOpen}
            setLocationPopupOpen={setLocationPopupOpen}
            mapTp={mapTp}
          />
          <hr className="divider" />
        </>
      )}

      {step === 'form' && (
        <section
          className={`panel-apply__user${embedded && isLoggedIn ? ' mgt-lg' : ''}${
            embedded ? ' panel-apply__user--embedded' : ''
          }`}
        >
          <h3 className="page-subtitle">신청자 정보</h3>
          <div className="panel-apply__form">
          {!isLoggedIn && (
            <>
              <div className="data">
                <div className="data__agree">
                <p className="msg-light">
                  대상자 여부 확인 및 참석 안내를 위해 아래와 같이 개인정보를 수집 · 이용하며, 동의하지 않으신 경우 좌담회에 신청하실 수 없습니다.
                  <br />
                  수집한 정보는 조사를 위한 목적으로만 사용됩니다.
                </p>
                <div className="data__table">
                  <table aria-label="개인정보 수집 안내">
                    <colgroup>
                      <col span={3} style={{ width: '33.33%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col">수집하는 개인정보</th>
                        <th scope="col">수집 및 이용목적</th>
                        <th scope="col">보유 및 이용기간</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>이름, 성별, 직업, 생년월일, 휴대폰정보</td>
                        <td>대상자 여부 확인 및 참석안내</td>
                        <td>수집일로부터 6개월</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="data__agree-head">
                  <label className="checkbox">
                    <input type="checkbox" checked={agreeYn} onChange={(e) => setAgreeYn(e.target.checked)} />
                    <strong className="checkbox__label color-primary">개인정보의 수집 · 이용에 관한 사항에 동의합니다.</strong>
                  </label>
                </div>
                </div>
              </div>
              <table className="data__table data__table--normal mgt-md" aria-label="신청자 정보 입력">
                <colgroup>
                  <col width="18%" />
                  <col width="auto" />
                </colgroup>
                <tbody>
                  <tr>
                    <th scope="row">
                      <label htmlFor="apply-name">이름</label>
                    </th>
                    <td>
                      <input
                        id="apply-name"
                        className="input panel-apply__field-name"
                        autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                        value={reqNm}
                        onChange={(e) => setReqNm(e.target.value)}
                        placeholder="이름을 입력해주세요."
                        readOnly={guestWriteLocked}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">성별</th>
                    <td>
                      <div className="form__group form__group--check">
                        <label className="radio">
                          <input
                            type="radio"
                            name="g"
                            checked={gender === '1'}
                            onChange={() => setGender('1')}
                            disabled={guestWriteLocked}
                          />
                          <span className="radio__label">남성</span>
                        </label>
                        <label className="radio">
                          <input
                            type="radio"
                            name="g"
                            checked={gender === '2'}
                            onChange={() => setGender('2')}
                            disabled={guestWriteLocked}
                          />
                          <span className="radio__label">여성</span>
                        </label>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <label htmlFor="apply-job">직업</label>
                    </th>
                    <td>
                      <select
                        id="apply-job"
                        className="select panel-apply__field-job"
                        autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                        value={jobCd}
                        onChange={(e) => setJobCd(e.target.value)}
                        disabled={guestWriteLocked}
                      >
                        <option value="">선택하세요</option>
                        {COMMON_CODE_JOB.map((j) => (
                          <option key={j.value} value={j.value}>
                            {j.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <label htmlFor="apply-birth-y">생년월일</label>
                    </th>
                    <td>
                      <div className="form__group">
                        <span className="group panel-apply__inline-group panel-apply__inline-group--birth">
                          <select
                            id="apply-birth-y"
                            className="select"
                            autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                            title="태어난 년도 선택"
                            value={birth1}
                            onChange={(e) => {
                              setBirth1(e.target.value);
                              setBirth3('');
                            }}
                            disabled={guestWriteLocked}
                          >
                            <option value="">(년도)</option>
                            {offlineBirthYearOptions.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          <select
                            id="apply-birth-m"
                            className="select"
                            autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                            title="태어난 달 선택"
                            value={birth2}
                            onChange={(e) => {
                              setBirth2(e.target.value);
                              setBirth3('');
                            }}
                            disabled={guestWriteLocked}
                          >
                            <option value="">(월)</option>
                            {BIRTH_MONTH_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          <select
                            id="apply-birth-d"
                            className="select"
                            autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                            title="태어난 날 선택"
                            value={birth3}
                            onChange={(e) => setBirth3(e.target.value)}
                            disabled={!birth1 || !birth2 || guestWriteLocked}
                          >
                            <option value="">(일)</option>
                            {offlineBirthDayOptions.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </span>
                      </div>
                      <span id="apply-birth-hint" className="form__help block">
                        주민등록상의 정보를 입력해주세요.
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <label htmlFor="apply-cell-1">휴대폰 정보</label>
                    </th>
                    <td>
                      <div className="form__group">
                        <span className="group panel-apply__inline-group panel-apply__inline-group--phone">
                          <select
                            id="apply-cell-1"
                            className="select"
                            autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                            title="휴대폰 앞자리 선택"
                            value={cellNo1}
                            onChange={(e) => setCellNo1(e.target.value)}
                            disabled={guestWriteLocked}
                          >
                            <option value="">(선택)</option>
                            {CELL_FIRST_NO_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          <span className="color-light-gray">-</span>
                          <input
                            id="apply-cell-2"
                            className="input"
                            autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                            title="휴대폰 중간자리 입력"
                            inputMode="numeric"
                            maxLength={4}
                            value={cellNo2}
                            readOnly={guestWriteLocked}
                            onChange={(e) => setCellNo2(e.target.value.replace(/\D/g, '').slice(0, 4))}
                          />
                          <span className="color-light-gray">-</span>
                          <input
                            id="apply-cell-3"
                            className="input"
                            autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                            title="휴대폰 끝자리 입력"
                            inputMode="numeric"
                            maxLength={4}
                            value={cellNo3}
                            readOnly={guestWriteLocked}
                            onChange={(e) => setCellNo3(e.target.value.replace(/\D/g, '').slice(0, 4))}
                          />
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
          {isLoggedIn && memberProfile && (
            <table className="data__table data__table--normal" aria-label="신청자 정보">
              <colgroup>
                <col width="18%" />
                <col width="auto" />
              </colgroup>
              <tbody>
                <tr>
                  <th scope="row">이름</th>
                  <td>{String(memberProfile.reqNm || '').trim() || String(user?.name || '').trim() || '—'}</td>
                </tr>
                <tr>
                  <th scope="row">
                    나이 및 <span className="iblock mgl-xs">성별</span>
                  </th>
                  <td>
                    {(() => {
                      const age = computeKoreanManAge(memberProfile.birthday);
                      const gName = commonCodeDbGenderName(memberProfile.gender);
                      const parts = [gName, age ? `만 ${age}세` : ''].filter(Boolean);
                      return parts.length ? parts.join(' / ') : '—';
                    })()}
                  </td>
                </tr>
                <tr>
                  <th scope="row">
                    휴대폰<span className="iblock mgl-xs">번호</span>
                  </th>
                  <td>{memberProfile.cellNo != null && String(memberProfile.cellNo).trim() ? memberProfile.cellNo : '—'}</td>
                </tr>
                <tr>
                  <th scope="row">직업</th>
                  <td>{commonCodeJobName(memberProfile.jobCd)}</td>
                </tr>
              </tbody>
            </table>
          )}
          {isLoggedIn && !memberProfile && (
            <div className="activity-list-loading activity-list-loading--inline" aria-busy="true" aria-live="polite">
              <Spinner size="sm" />
            </div>
          )}

          <>
          <table className="data__table data__table--normal mgt-lg" aria-label="신청 정보">
            <colgroup>
              <col width="18%" />
              <col width="auto" />
            </colgroup>
            <tbody>
              {step === 'form' && !isLoggedIn && needsIntroducer(detail) && (
                <tr>
                  <th scope="row">
                    <label htmlFor="apply-inv-user">소개자</label>
                  </th>
                  <td>
                    <div className="form__group flex-column">
                      <div className="form__group">
                        <span className="group">
                          <input
                            id="apply-inv-user"
                            className="input"
                            autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                            value={invUserId}
                            onChange={(e) => {
                              setInvUserId(e.target.value);
                              setInvVerified(false);
                            }}
                            placeholder="소개자 아이디 입력"
                            aria-label="소개자 아이디"
                            disabled={noneInv || guestWriteLocked}
                          />
                          <button type="button" className="btn btn--form btn--light" onClick={checkInviteId} disabled={noneInv || guestWriteLocked}>
                            ID 확인
                          </button>
                        </span>
                        <span className={`group${isMobile ? ' full' : ''}`}>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              checked={noneInv}
                              onChange={(e) => setNoneInv(e.target.checked)}
                              disabled={guestWriteLocked}
                            />
                            <small className="checkbox__label small">소개자 없음</small>
                          </label>
                        </span>
                      </div>
                      <span className="form__help block">신청 완료 후엔 소개가 인정되지 않으니, 소개하신 분의 아이디를 꼭 입력바랍니다.</span>
                    </div>
                  </td>
                </tr>
              )}
              <tr>
                <th scope="row">공통대상</th>
                <td>
                  {replaceTgtDescForForm ? (
                    <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{replaceTgtDescForForm}</p>
                  ) : null}
                </td>
              </tr>
              {step === 'form' && showNewFlowSelectionSummary && selectedGroupSummaryLabels.length > 0 && (
                <tr>
                  <th scope="row">선택한 그룹</th>
                  <td>
                    {selectedGroupSummaryLabels.map((nm, idx) => (
                      <p key={`grp-sum-${idx}`} style={{ margin: idx === 0 ? 0 : undefined }} className={idx > 0 ? 'mgt-xs' : undefined}>
                        [그룹{idx + 1}] {nm}
                      </p>
                    ))}
                  </td>
                </tr>
              )}
              {step === 'form' &&
                showNewFlowSelectionSummary &&
                preAnswerDisplayRows.map((row) => (
                  <tr key={`pre-ans-${row.questNoText}`}>
                    <th scope="row">{row.questNoText}</th>
                    <td>
                      {row.questText ? <p className="mgb-sm">{row.questText}</p> : null}
                      {row.ansTxt.map((ans, idx) => (
                        <p
                          key={`${row.questNoText}-ans-${idx}`}
                          className={`color-primary${idx > 0 ? ' mgt-xs' : ''}`}
                          style={{ margin: idx === 0 && !row.questText ? 0 : undefined }}
                        >
                          {ans}
                        </p>
                      ))}
                    </td>
                  </tr>
                ))}
              {step === 'form' && showGroupRow && (
                <tr>
                  <th scope="row">그룹선택</th>
                  <td>
                    <div className="form__group form__group--check flex-column">
                      {sortedGrpList(detail).map((g) => {
                        const grpMultiSelect = detail.grpMultiYn === 'Y';
                        const inputType = grpMultiSelect ? 'checkbox' : 'radio';
                        const labelClass = grpMultiSelect ? 'checkbox' : 'radio';
                        const labelTextClass = grpMultiSelect ? 'checkbox__label' : 'radio__label';
                        return (
                          <label key={g.grpOrd} className={labelClass}>
                            <input
                              type={inputType}
                              name="grp"
                              checked={!!grpPick[g.grpOrd]}
                              disabled={g.closYn === 'Y'}
                              onChange={(e) => {
                                if (grpMultiSelect) {
                                  setGrpPick((prev) => ({ ...prev, [g.grpOrd]: e.target.checked }));
                                } else {
                                  setGrpPick({ [g.grpOrd]: true });
                                }
                              }}
                            />
                            <span className={`${labelTextClass} ${g.closYn === 'Y' ? 'color-light-gray' : ''}`}>
                              {g.grpNm}
                              {g.closYn === 'Y' && <span className="color-danger mgl-sm">[마감]</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              )}
              {step === 'form' && showLegacyQuestionRows &&
                qList.map((q) => (
                  <tr key={q.qestnOrd}>
                    <th scope="row">질문 {q.qestnOrd}</th>
                    <td>
                      <p className="mgb-sm">{q.qestnNm}</p>
                      <textarea
                        className="textarea textarea--quiz full"
                        autoComplete={APPLY_TEXT_AUTOCOMPLETE}
                        rows={5}
                        value={legacyAnswers[q.qestnOrd] || ''}
                        onChange={(e) =>
                          setLegacyAnswers((prev) => ({
                            ...prev,
                            [q.qestnOrd]: e.target.value,
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </>

          {isMobile && <hr className="divider" />}
          <div className="box__info flex-center">
            {showPreSurveyCompleteNotice && (
              <p className="strong-text" style={{ margin: 0 }}>
                사전설문에 모두 응답하셨습니다. 신청하기를 눌러 좌담회에 최종 참여해주세요.
              </p>
            )}
            <p>
              검증을 통해 내용이 사실과 다른 경우, 좌담회 참석이 <span className="iblock">취소 되거나</span> 참여 사례금을
              지급하지 않습니다.
            </p>
          </div>

          <div className="data__action-buttons">
            {!isLoggedIn && !embedded && (
              <button
                type="button"
                className="btn btn--lg btn--outline-light"
                onClick={() => (onExitApply ? onExitApply() : navigate(paths.survey.panelDetail(detail.srvNo)))}
              >
                이전
              </button>
            )}
            <button
              type="button"
              className="btn btn--lg btn--outline-light btn--panel-offline-secondary"
              onClick={openCancelApplyFlow}
            >
              취소하기
            </button>
            {showFormNextBtn && (
              <button
                type="button"
                className="btn btn--lg btn--primary"
                disabled={submitting || (isLoggedIn && !memberProfile)}
                onClick={handleWriteNext}
              >
                다음
              </button>
            )}
            {showFormSubmitBtn && (
              <button
                type="button"
                className="btn btn--lg btn--primary"
                disabled={submitting}
                onClick={handleWriteSubmit}
              >
                신청하기
              </button>
            )}
          </div>
          </div>
        </section>
      )}

      {step === 'questions' && currentQuest && (
        <section className="panel-apply__confirm">
          <div className="panel-apply__form">
          <table className="data__table data__table--normal first-line" aria-label="신청자 정보 / 질의응답">
            <colgroup>
              <col width="18%" />
              <col width="auto" />
            </colgroup>
            <tbody>
              {renderApplicantSummaryRows()}
              <tr>
                <th scope="row">공통대상</th>
                <td>
                  {replaceTgtDescForForm ? (
                    <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{replaceTgtDescForForm}</p>
                  ) : null}
                </td>
              </tr>
            </tbody>
            <tbody>
              {isMobile && <hr className="divider" />}
              <tr>
                <th scope="row">
                  {qIndex + 1}/{qList.length}
                </th>
                <td>{currentQuest.questText}</td>
              </tr>
              <tr>
                <td colSpan={2}>
                  <dl className="quiz__item">
                    <dt>{currentQuest.questNoText}</dt>
                    <dd>{renderQuestionInputs()}</dd>
                  </dl>
                  <div className="quiz__item-buttons">
                    <button type="button" className="btn btn--md btn--primary" onClick={submitQuestionNext}>
                      다음
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {isMobile && <hr className="divider" />}
          <div className="box__info flex-center">
            <p>
              검증을 통해 내용이 사실과 다른 경우, 좌담회 참석이 <span className="iblock">취소 되거나</span> 참여 사례금을
              지급하지 않습니다.
            </p>
          </div>
          <div className="data__action-buttons">
            <button type="button" className="btn btn--lg btn--light" onClick={openCancelApplyFlow}>
              취소하기
            </button>
            <button type="button" className="btn btn--lg btn--primary" disabled>
              신청하기
            </button>
          </div>
          </div>
        </section>
      )}

      {step === 'confirm' && confirmData && (
        <section className="panel-apply__confirm">
          <h3 className="page-subtitle">신청 내용 확인</h3>
          <table className="data__table data__table--normal first-line">
            <tbody>
              {confirmData.__legacyLocal ? (
                <>
                  {!confirmData.reqInfo?.reqId && (
                    <>
                      <tr>
                        <th>이름</th>
                        <td>{confirmData.reqInfo?.reqNm}</td>
                      </tr>
                      {confirmData.legacyGuestDetail && (
                        <>
                          <tr>
                            <th>성별</th>
                            <td>{confirmData.legacyGuestDetail.genderLabel}</td>
                          </tr>
                          <tr>
                            <th>직업</th>
                            <td>{confirmData.legacyGuestDetail.jobLabel}</td>
                          </tr>
                          <tr>
                            <th>생년월일</th>
                            <td>{confirmData.legacyGuestDetail.birthLabel}</td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <th>휴대폰</th>
                        <td>{confirmData.reqInfo?.cellNo}</td>
                      </tr>
                    </>
                  )}
                  {confirmData.reqInfo?.reqId && (
                    <tr>
                      <th>신청자</th>
                      <td>
                        {confirmData.reqInfo.reqNm || '—'}
                        <span className="block color-light-gray mgt-xs">패널 계정으로 신청합니다.</span>
                      </td>
                    </tr>
                  )}
                  {(confirmData.grpList || []).length > 0 && (
                    <tr>
                      <th>선택 그룹</th>
                      <td>
                        {(confirmData.grpList || [])
                          .map((g) => g.grpNm || g.grpOrd)
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </td>
                    </tr>
                  )}
                  {(confirmData.legacyQestnRows || []).map((row) => (
                    <tr key={row.qestnOrd}>
                      <th>질문 {row.qestnOrd}</th>
                      <td>
                        <p className="mgb-sm">{row.qestnNm}</p>
                        <p style={{ margin: 0 }}>{row.answerCont}</p>
                      </td>
                    </tr>
                  ))}
                  {confirmData.legacyIntro && (
                    <tr>
                      <th>소개자</th>
                      <td>
                        {confirmData.legacyIntro.noneInv || !confirmData.legacyIntro.invUserId
                          ? '(소개자 없음)'
                          : confirmData.legacyIntro.invUserId}
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  {confirmData.reqInfo && !confirmData.reqInfo.reqId && (
                    <>
                      <tr>
                        <th>이름</th>
                        <td>{confirmData.reqInfo.reqNm}</td>
                      </tr>
                      <tr>
                        <th>휴대폰</th>
                        <td>{confirmData.reqInfo.cellNo}</td>
                      </tr>
                    </>
                  )}
                  {confirmData.reqInfo && confirmData.reqInfo.reqId && (
                    <tr>
                      <th>신청자</th>
                      <td>
                        {confirmData.reqInfo.reqNm || '—'}
                        <span className="block color-light-gray mgt-xs">패널 계정으로 신청합니다.</span>
                      </td>
                    </tr>
                  )}
                  {(confirmData.grpList || []).length > 0 && (
                    <tr>
                      <th>선택 그룹</th>
                      <td>
                        {(confirmData.grpList || [])
                          .map((g) => g.grpNm || g.grpOrd)
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </td>
                    </tr>
                  )}
                  {(confirmData.answerList || []).length > 0 && (
                    <tr>
                      <th>사전 응답</th>
                      <td>
                        <ul className="info-list mgt-xs">
                          {(confirmData.answerList || []).map((row, idx) => (
                            <li key={`${row.questNoText}-${row.ansNo}-${idx}`}>
                              <span className="color-light-gray">{row.ansNo}</span>: {row.ansValue}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
          <div className="panel-apply__confirm-guide">
            <p>{OFFLINE_APPLY_COMPLETE_GUIDE}</p>
          </div>
          <div className="data__action-buttons mgt-md">
            {!newFlow && (
              <button
                type="button"
                className="btn btn--md btn--outline-light"
                onClick={() => {
                  setConfirmData(null);
                  setStep('form');
                }}
              >
                이전
              </button>
            )}
            <button
              type="button"
              className="btn btn--lg btn--outline-light btn--panel-offline-secondary"
              onClick={openCancelApplyFlow}
            >
              취소하기
            </button>
            <button type="button" className="btn btn--lg btn--primary" disabled={submitting} onClick={finalConfirm}>
              신청완료
            </button>
          </div>
        </section>
      )}
    </>
  );

  return embedded ? main : <div className="panel-list-page">{main}</div>;
}

function sortedGrpList(detail) {
  return [...(detail.grpList || [])]
    .filter(() => detail.grpMultiYn !== 'E')
    .sort((a, b) => (Number(a.grpOrd) || 0) - (Number(b.grpOrd) || 0));
}
